import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { GuidedSetupStore, SetupPhase } from '../guided-setup/guided-setup.store';
import { languageTagFor } from '../guided-setup/guided-setup-dialog';
import {
	formatMonthlyPrice,
	SetupSignals,
	sipAccountIsOnOffer,
} from '../shared/helpers/guided-setup.helper';
import { FeatureService } from '../shared/services/feature.service';
import { SipOrderLinkService } from '../shared/services/sip-order-link.service';
import {
	LINK_OFFER_STATUS_COMPLETED,
	LINK_OFFER_STATUS_OPEN,
	LINK_OFFER_STATUS_ADDRESS_INCOMPLETE,
	LINK_OFFER_STATUS_MULTIPLE_PHONE_SERVICES,
	LinkOrderOffer,
} from '../shared/models/sip-order-link.model';

/**
 * The page a SIP order link opens (`?sipOrder&uid=<uid>`): a frame around the dialog's `GuidedSetup`,
 * entered as `link`. HTTP errors and offers without texts are handled by `FeatureService`.
 */
@Component({
	selector: 'app-sip-order',
	templateUrl: './sip-order.html',
	styleUrl: './sip-order.scss',
	standalone: false,
	changeDetection: ChangeDetectionStrategy.OnPush,
	/** The copy is in the account's language while `index.html` says `en` (same as the dialog, M17). */
	host: { '[attr.lang]': 'lang()' },
})
export class SipOrder {
	public readonly store: GuidedSetupStore = inject(GuidedSetupStore);
	private readonly sipOrderLink: SipOrderLinkService = inject(SipOrderLinkService);
	private readonly featureService: FeatureService = inject(FeatureService);

	/** The offer once it has arrived; null while it is in flight. */
	private readonly offer = toSignal(this.featureService.sipOrderOffer$, { initialValue: null });

	/** BCP 47 tag of the account's language, or null (then the page's own `lang` applies). */
	public readonly lang = computed(() => languageTagFor(this.offer()?.languageId));

	/** The monthly price, formatted like `CallbackOnCallnote.setupMonthlyCost`. */
	public readonly monthlyCost = computed(() => {
		const offer = this.offer();
		return formatMonthlyPrice(
			offer?.monthlyPrice,
			offer?.currency,
			languageTagFor(offer?.languageId) ?? 'de-DE',
		);
	});

	/** `GuidedSetup`'s required input; decides nothing here, the run starts already active. */
	public readonly signals = computed<SetupSignals>(() => ({
		hasSipNumber: false,
		hasSipExtension: false,
		outboundOutgoingPhoneNumber: null,
		sipExtensionPrice: this.offer()?.monthlyPrice ?? null,
		currency: this.offer()?.currency ?? null,
	}));

	constructor() {
		/** The store is root-provided; `active` makes `GuidedSetup.ngOnInit` keep these `displaySteps`. */
		this.store.reset();
		this.store.displaySteps.set(['orderSipAccount', 'confirmation']);
		this.store.active.set(true);
		this.store.phase.set('resolving');

		this.featureService.sipOrderOffer$
			.pipe(takeUntilDestroyed(inject(DestroyRef)))
			.subscribe((offer) => this.enter(offer));
	}

	/** Moves the wizard to the phase the offer calls for. */
	private enter(offer: LinkOrderOffer): void {
		const phase = phaseFor(offer);
		if (phase === 'order') {
			this.store.companyName.set(offer.companyName?.trim() || null);
		} else {
			/** Nothing left to order with this link on any other screen. */
			this.sipOrderLink.clear();
		}
		if (phase === 'booked') {
			this.store.sipOrdered.set(true);
		}
		this.store.phase.set(phase);
	}
}

/** The phase for an offer; OPEN without a displayable price fails closed to `linkExpired`. */
export function phaseFor(offer: LinkOrderOffer): SetupPhase {
	switch (offer.status) {
		case LINK_OFFER_STATUS_OPEN:
			return sipAccountIsOnOffer(offer.monthlyPrice, offer.currency) ? 'order' : 'linkExpired';
		case LINK_OFFER_STATUS_COMPLETED:
			return 'booked';
		case LINK_OFFER_STATUS_MULTIPLE_PHONE_SERVICES:
		case LINK_OFFER_STATUS_ADDRESS_INCOMPLETE:
			return 'linkNotOrderable';
		default:
			return 'linkExpired';
	}
}
